/** Billing support WhatsApp for scheduled-change reversals and overrides. */
export const BILLING_SUPPORT_WHATSAPP_DISPLAY = "+55 31 99083-5209";
export const BILLING_SUPPORT_WHATSAPP_PHONE = "5531990835209";

/** Sales WhatsApp on the public plans page. */
export const SALES_WHATSAPP_DISPLAY = "+55 11 91195-0246";
export const SALES_WHATSAPP_PHONE = "5511911950246";

export type BillingSupportIntent =
  | "scheduled_change_help"
  | "cancel_scheduled_change"
  | "replace_scheduled_change"
  | "reverse_openpix_cancellation"
  | "cancel_rescue"
  | "change_plan";

type TranslateFn = (key: string, opts?: Record<string, string>) => string;

/** Build wa.me link with optional pre-filled message. */
export function buildWhatsAppUrl(phoneDigits: string, message?: string): string {
  const phone = phoneDigits.replace(/\D/g, "");
  const base = `https://wa.me/${phone}`;
  if (!message?.trim()) return base;
  return `${base}?text=${encodeURIComponent(message.trim())}`;
}

export const BILLING_SUPPORT_WHATSAPP_URL = buildWhatsAppUrl(BILLING_SUPPORT_WHATSAPP_PHONE);
export const SALES_WHATSAPP_URL = buildWhatsAppUrl(SALES_WHATSAPP_PHONE);

const BILLING_INTENT_KEYS: Record<BillingSupportIntent, string> = {
  scheduled_change_help: "integrated.whatsappSupportMessageIntentHelp",
  cancel_scheduled_change: "integrated.whatsappSupportMessageIntentCancel",
  replace_scheduled_change: "integrated.whatsappSupportMessageIntentReplace",
  reverse_openpix_cancellation: "integrated.whatsappSupportMessageIntentReverseOpenPix",
  cancel_rescue: "integrated.whatsappSupportMessageIntentCancelRescue",
  change_plan: "integrated.whatsappSupportMessageIntentChangePlan",
};

/** Pre-filled billing support message for WhatsApp (email + intent + optional change details). */
export function formatBillingSupportWhatsAppMessage(
  t: TranslateFn,
  args: {
    email: string;
    intent: BillingSupportIntent;
    scheduledChangeDetails?: string;
    requestedChangeDetails?: string;
    cancellationReasonDetails?: string;
  },
): string {
  const lines = [
    t("integrated.whatsappSupportMessageIntro"),
    t("integrated.whatsappSupportMessageEmail", { email: args.email }),
    "",
    t(BILLING_INTENT_KEYS[args.intent]),
  ];

  if (args.scheduledChangeDetails?.trim()) {
    lines.push(
      t("integrated.whatsappSupportMessageScheduledChange", {
        details: args.scheduledChangeDetails.trim(),
      }),
    );
  }

  if (args.requestedChangeDetails?.trim()) {
    lines.push(
      t("integrated.whatsappSupportMessageRequestedChange", {
        details: args.requestedChangeDetails.trim(),
      }),
    );
  }

  if (args.cancellationReasonDetails?.trim()) {
    lines.push(
      t("integrated.whatsappSupportMessageCancelReason", {
        details: args.cancellationReasonDetails.trim(),
      }),
    );
  }

  return lines.join("\n");
}

export function buildBillingSupportWhatsAppUrl(
  t: TranslateFn,
  args: Parameters<typeof formatBillingSupportWhatsAppMessage>[1],
): string {
  return buildWhatsAppUrl(
    BILLING_SUPPORT_WHATSAPP_PHONE,
    formatBillingSupportWhatsAppMessage(t, args),
  );
}

/** Pre-filled sales message for the public plans page. */
export function formatSalesWhatsAppMessage(t: TranslateFn, email?: string): string {
  const lines = [t("plansPage.whatsappSupportMessageIntro")];
  if (email?.trim()) {
    lines.push(t("plansPage.whatsappSupportMessageEmail", { email: email.trim() }));
  }
  lines.push("", t("plansPage.whatsappSupportMessageIntent"));
  return lines.join("\n");
}

export function buildSalesWhatsAppUrl(t: TranslateFn, email?: string): string {
  return buildWhatsAppUrl(SALES_WHATSAPP_PHONE, formatSalesWhatsAppMessage(t, email));
}
